<template>
    <div>
        <div class="flex flex-col min-h-0 h-full p-8">
            <page-header :title="$t('global.mainNav.payments')">
                <nio-button color="blue" icon="regular/add" @click="triggerCreate">
                    {{ $t('global.general.add') }}
                </nio-button>
            </page-header>

            <content-box :loading="$wait.is('fetching.payments')">
                <template #header>
                    <content-box-tabs :active-tab.sync="filters.tab" :tabs="tabs" />
                    <content-box-search :placeholder="$t('global.general.search')" v-model="filters.search" />
                </template>
                <template #body>
                    <nio-table
                        :columns="columns"
                        :rows="payments"
                        ref="body"
                    >
                        <template #no-results>
                            <figure class="mb-6 text-3xl select-none">
                                ¯\_(ツ)_/¯
                            </figure>
                            <p class="mb-1 text-xl">
                                {{ $t('payments.paymentsList.noPayments') }}
                            </p>
                            <p class="mb-4">
                                {{ $t('payments.paymentsList.wouldYouLikeToAddOne') }}
                            </p>
                            <nio-button color="blue" icon="regular/add" @click="triggerCreate">
                                {{ $t('global.general.add') }}
                            </nio-button>
                        </template>

                        <template v-slot:[`column-${column.name}`]="{ row: payment }" v-for="column in columns">
                            <payments-list-item
                                :column="column.name"
                                :payment="payment"
                                :truncate="column.truncate"
                                :key="column.name"
                                @delete="triggerDelete(payment)"
                                @update="triggerUpdate(payment)"
                            />
                        </template>
                    </nio-table>
                </template>
                <template #footer v-if="payments.length">
                    <div class="flex-1 flex items-center justify-start p-4">
                        <!-- -->
                    </div>

                    <div class="flex-1 flex items-center justify-center p-4">
                        <pagination
                            :current-page="pagination.currentPage"
                            :last-page="pagination.lastPage"
                            @update="setPaginationPage"
                            v-if="pagination.total"
                        />
                    </div>

                    <div class="flex-1 flex items-center justify-end p-4">
                        <pagination-info
                            :current="pagination.currentPage"
                            :per-page="perPage"
                            :total="pagination.total"
                            v-if="pagination.total"
                        />
                    </div>
                </template>
            </content-box>
        </div>

        <payment-modal />
        <confirm-modal
            :opened.sync="openDelete"
            :title="$t('payments.paymentsList.deleteModal.header')"
            @confirm="deleteSelected"
            @deny="toDelete = null"
        >
            <p slot="message">
                {{ $t('payments.paymentsList.deleteModal.message') }}
            </p>
        </confirm-modal>

        <filters-in-url :filters="filters" />
    </div>
</template>

<script>
    import debounce from 'lodash/debounce.js';
    import {
        mapState,
        mapActions,
        mapMutations,
    } from 'vuex';
    import ConfirmModal from '@/components/Modals/ConfirmModal.vue';
    import ContentBox from '@/components/ContentBox.vue';
    import ContentBoxSearch from '@/components/ContentBoxSearch.vue';
    import ContentBoxTabs from '@/components/ContentBoxTabs.vue';
    import FiltersInUrl from '@/components/Utils/FiltersInUrl.js';
    import PageHeader from '@/components/PageHeader.vue';
    import Pagination from '@/components/Pagination.vue';
    import PaginationInfo from '@/components/PaginationInfo.vue';
    import PaymentModal from '@/components/Modals/PaymentModal.vue';
    import PaymentsListItem from '@/views/Payments/PaymentsListItem.vue';

    export default {
        components: {
            ConfirmModal,
            ContentBox,
            ContentBoxSearch,
            ContentBoxTabs,
            FiltersInUrl,
            PageHeader,
            Pagination,
            PaginationInfo,
            PaymentModal,
            PaymentsListItem,
        },

        data() {
            return {
                loaded: false,
                openDelete: false,
                toDelete: null,
                perPage: 25,
                filters: {
                    search: '',
                    tab: 'all',
                },
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            ...mapState('payment', [
                'payments',
                'pagination',
            ]),

            paid() {
                switch (this.filters.tab) {
                    case 'received':
                        return true;
                    case 'pending':
                        return false;
                    default:
                        return null;
                }
            },

            tabs() {
                return [
                    {
                        value: 'all',
                        text: this.$t('payments.paymentsList.allTab'),
                        icon: 'regular/navigation-menu',
                    },
                    {
                        value: 'received',
                        text: this.$t('payments.paymentsList.receivedTab'),
                        icon: 'regular/check-circle-1',
                    },
                    {
                        value: 'pending',
                        text: this.$t('payments.paymentsList.pendingTab'),
                        icon: 'regular/remove-circle',
                    },
                ];
            },

            columns() {
                return [
                    {
                        name: 'number',
                        label: this.$t('payments.paymentsList.number'),
                        icon: 'regular/hash',
                        className: 'flex-2',
                    },
                    {
                        name: 'contact',
                        label: this.$t('payments.paymentsList.contact'),
                        icon: 'regular/single-neutral',
                        className: 'flex-3',
                    },
                    {
                        name: 'amount',
                        label: this.$t('payments.paymentsList.amount'),
                        icon: 'regular/money-bag-dollar',
                        className: 'flex-2',
                    },
                    {
                        name: 'status',
                        label: this.$t('payments.paymentsList.status'),
                        icon: 'regular/view-1',
                        className: 'flex-1',
                        truncate: false,
                    },
                    {
                        name: 'actions',
                        label: this.$t('payments.paymentsList.actions'),
                        icon: 'regular/flash-1',
                        className: 'flex-1',
                        align: 'center',
                        truncate: false,
                    },
                ];
            },
        },

        watch: {
            'pagination.currentPage'() {
                this.scrollTop();
                this.fetch();
            },

            'filters.tab'() {
                this.fetch();
            },

            'filters.search'() {
                this.$wait.start('fetching.payments');

                if (this.loaded) {
                    this.debounceFetch();
                } else {
                    this.fetch();
                }
            },

            'authUser.account.id'(newValue) {
                if (!newValue) {
                    return;
                }

                this.fetch();
            },

            'authUser.account.payment_module': {
                immediate: true,
                handler(newValue) {
                    if (newValue) {
                        return;
                    }

                    this.$router.replace({ name: 'chat' });
                },
            },
        },

        methods: {
            ...mapActions('payment', [
                'loadPayments',
                'deletePayment',
            ]),

            ...mapMutations('payment', [
                'setPaginationPage',
                'addPayment',
            ]),

            reset() {
                this.toDelete = null;
            },

            async fetch() {
                this.$wait.start('fetching.payments');

                const payload = {
                    search: this.filters.search || null,
                    paid: this.paid,
                    page: this.pagination.currentPage,
                    perPage: this.perPage,
                };

                this.reset();

                try {
                    await this.loadPayments(payload);
                } finally {
                    this.$wait.end('fetching.payments');
                }
            },

            debounceFetch() {
                this.setPaginationPage(1);
                this.fetch();
            },

            triggerCreate() {
                this.$modal.open('payment');
            },

            triggerUpdate(payment) {
                this.$modal.open('payment', {
                    contact: payment.contact,
                    payment,
                });
            },

            triggerDelete(payment) {
                this.toDelete = payment;
                this.openDelete = true;
            },

            async deleteSelected() {
                const deletePromises = [];

                const payments = [this.toDelete];

                payments.forEach(payment => {
                    deletePromises.push(this.deletePayment(payment.id));
                });

                try {
                    await Promise.all(deletePromises);

                    this.$notify.show({
                        type: 'success',
                        message: this.$tc('payments.paymentsList.successes.deleted', deletePromises.length),
                    });

                    this.reset();
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$tc('payments.paymentsList.errors.deleting', deletePromises.length),
                    });
                }
            },

            scrollTop() {
                if (this.$refs.body) {
                    this.$refs.body.$el.scrollTop = 0;
                }
            },
        },

        created() {
            this.debounceFetch = debounce(this.debounceFetch, 300);
        },

        mounted() {
            this.fetch();

            this.$bus.on('payment.created', this.add);
            this.$bus.on('payment.updated', this.update);

            this.$nextTick(() => {
                this.loaded = true;
            });
        },

        beforeDestroy() {
            this.$bus.off('payment.created', this.add);
            this.$bus.off('payment.updated', this.update);
        },
    };
</script>
